import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FieldValues, useFormContext } from 'react-hook-form'
import {
  Button,
  CreatableSelectInput,
  TextInput,
  ToggleInput,
  Table,
} from '@demandlocal/components'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from './CreateNewCampaignForm'
import { ReactComponent as PinIcon } from '../../../images/icons/pin.svg'
import { ReactComponent as GoogleIcon } from '../../../images/icons/audience_google.svg'
import { ReactComponent as BasisLogo } from '../../../images/icons/audience_basis.svg'
import { ReactComponent as MailchimpIcon } from '../../../images/icons/audience_mailchimp.svg'
import { ReactComponent as FacebookIcon } from '../../../images/icons/audience_facebook.svg'
import { ReactComponent as TradeDeskIcon } from '../../../images/icons/audience_tradedesk.svg'
import { ReactComponent as AmazonIcon } from '../../../images/icons/audience_amazon.svg'
import { ReactComponent as AudienceIcon } from '../../../images/icons/announcement_round.svg'
import dayjs from 'dayjs'
import { Row, createColumnHelper } from '@tanstack/react-table'
import { Audience } from '../../../lib/types/Audience'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useGetAudiences } from '../../../api/dealership/customers/audiences/useGetAudiences'
import { ReactComponent as CalendarIcon } from '../../../images/icons/calendar_icon.svg'
import { useEditLinkCampaign } from '../../../api/dealership/campaigns/useEditLinkCampaign'
import { ReactComponent as DollarSign } from '../../../images/icons/dollar_sign.svg'
import dmaRegions from '../../../lib/utils/dmaRegions'
import { get } from 'lodash'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { useNavigate } from 'react-router'
import { useDealership } from '../../../store/zustand/dealershipStore'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import { Link } from 'react-router-dom'
dayjs.extend(advancedFormat)

const dmaZipCodes = (): Promise<typeof import('../../../lib/utils/dmaZipCodes')> =>
  import('../../../lib/utils/dmaZipCodes')
type Props = {
  step: number
  setStep: (step: number) => void
}

const columnHelper = createColumnHelper<
  Audience & { mappedAudiences: Record<string, number> }
>()

const AudienceSubComponentDetails = ({
  row,
}: {
  row: Row<Audience & { mappedAudiences: Record<string, number> }>
}): JSX.Element => {
  return (
    <div className={'grid grid-cols-3 gap-3 bg-gainsboro-gray/30 p-3'}>
      <div className={'px-3 py-4 flex flex-col gap-3 border-2 rounded-xl'}>
        <span className="text-sm">Audience size</span>
        <span className={'text-md font-bold'}>
          {formatNumber(row.original.audience_size || 0)}
        </span>
      </div>
      <div className={'px-3 py-4 flex flex-col gap-3 border-2 rounded-xl'}>
        <span className="text-sm">Linked destinations</span>
        <div className={'flex flex-row gap-3'}>
          {Boolean(row.original.mappedAudiences.facebook) && (
            <FacebookIcon className={'w-6 h-6'} />
          )}
          {Boolean(row.original.mappedAudiences.google) && (
            <GoogleIcon className={'w-6 h-6'} />
          )}
          {Boolean(row.original.mappedAudiences.mailchimp) && (
            <MailchimpIcon className={'w-6 h-6'} />
          )}
          {Boolean(row.original.mappedAudiences.tradedesk) && (
            <TradeDeskIcon className={'w-6 h-6'} />
          )}
          {Boolean(row.original.mappedAudiences.basis) && (
            <BasisLogo className={'w-6 h-6'} />
          )}
          {Boolean(row.original.mappedAudiences.amazon) && (
            <AmazonIcon className={'w-6 h-6'} />
          )}
        </div>
      </div>
      <div className={'px-3 py-4 flex flex-col gap-3 border-2 rounded-xl'}>
        <span className="text-sm">Last update</span>
        <span className={'text-md font-bold'}>
          {dayjs(row.original.last_run).format('MMM Do, YYYY')}
        </span>
      </div>
    </div>
  )
}

const CreateNewCampaignDetails = ({ step, setStep }: Props): JSX.Element => {
  const recommended_budget = '5000'
  const [selectedAudiences, setSelectedAudiences] = useState<
    Array<Record<string, string | number>>
  >([])
  const formContext = useFormContext()
  const navigate = useNavigate()
  const [containerHeight, setContainerHeight] = React.useState(0)
  const [foundDMA, setFoundDMA] = React.useState<string>('')
  const [showDMATool, setShowDMATool] = React.useState<boolean>(false)
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { handleSubmit, setValue, watch } = formContext
  const formData = formContext.watch()
  const dealership = useDealership()

  const dmaOptions = useMemo(() => {
    return dmaRegions.map((region) => ({
      label: region.dma,
      value: region.dma,
    }))
  }, [])

  const { mutateAsync: saveLinkCampaign, isLoading } = useEditLinkCampaign()

  const onSubmit = useCallback(
    async (values: Partial<FieldValues>): Promise<void> => {
      setFormValues((prevFormValues) => {
        return {
          ...prevFormValues,
          start_date: dayjs(values?.start_date).format('MM/DD/YYYY'),
          end_date: dayjs(values?.end_date).format('MM/DD/YYYY'),
          budget: values.use_recommended_budget ? recommended_budget : values.budget,
          campaign_name: values.campaign_name,
          target: {
            zip: values?.zip?.map((dma: { value: string }) => dma.value).join(','),
            use_custom_audience: values?.use_custom_audience,
            audience: values?.use_custom_audience ? selectedAudiences : [],
          },
        }
      })

      if (formValues.campaign_id) {
        await saveLinkCampaign({
          data: {
            start_date: dayjs(values?.start_date).format('YYYY-MM-DD'),
            end_date: dayjs(values?.end_date).format('YYYY-MM-DD'),
            zip: values?.zip?.map((dma: { value: string }) => dma.value).join(','),
            campaign_display_name: values.campaign_name,
            budget: values.use_recommended_budget ? recommended_budget : values.budget,
          },
          link_campaign_id: formValues.campaign_id,
        })
      }

      setStep(step + 1)
    },
    [
      formValues.campaign_id,
      saveLinkCampaign,
      selectedAudiences,
      setFormValues,
      setStep,
      step,
    ],
  )

  const submitOnEnter = useCallback(
    (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit(onSubmit)()
      }
    },
    [handleSubmit, onSubmit],
  )

  useEffect(() => {
    document.addEventListener('keydown', submitOnEnter)
    return () => document.removeEventListener('keydown', submitOnEnter)
  }, [submitOnEnter])

  const toggleAudienceSelection = useCallback(
    ({ audience_name, size }: { audience_name: string; size: number }) => {
      if (selectedAudiences.some((audience) => audience.name === audience_name)) {
        setSelectedAudiences(
          selectedAudiences.filter((audience) => audience.name !== audience_name),
        )
      } else {
        setSelectedAudiences([
          ...selectedAudiences,
          { name: audience_name, size: Number(size) },
        ])
      }
    },
    [selectedAudiences],
  )

  useEffect(() => {
    if (formData?.date_preset) {
      setValue(
        'start_date',
        formData?.date_preset === 'eom'
          ? dayjs().format('YYYY-MM-DD')
          : dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD'),
      )
      setValue(
        'end_date',
        formData?.date_preset === 'eom'
          ? dayjs().endOf('month').format('YYYY-MM-DD')
          : dayjs().add(1, 'month').endOf('month').format('YYYY-MM-DD'),
      )
    }
  }, [formData?.date_preset, setValue])

  useEffect(() => {
    if (formValues?.target?.zip) {
      setValue(
        'zip',
        dmaOptions?.filter((region: { label: string; value: string }) =>
          formValues?.target?.zip?.split(',').includes(region?.value),
        ),
      )
    }
    if (formValues?.campaign_name) setValue('campaign_name', formValues?.campaign_name)
    if (formValues?.budget) setValue('budget', formValues?.budget)
    if (formValues?.start_date) {
      setValue('start_date', dayjs(formValues?.start_date).format('YYYY-MM-DD'))
      setValue('end_date', dayjs(formValues?.end_date).format('YYYY-MM-DD'))
    }
  }, [setValue, formValues, dmaOptions])

  useEffect(() => {
    if (
      dayjs(formData.start_date).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') &&
      dayjs(formData.end_date).format('YYYY-MM-DD') ===
        dayjs().endOf('month').format('YYYY-MM-DD')
    )
      setValue('date_preset', 'eom')
    else if (
      dayjs(formData.start_date).format('YYYY-MM-DD') ===
        dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD') &&
      dayjs(formData.end_date).format('YYYY-MM-DD') ===
        dayjs().add(1, 'month').endOf('month').format('YYYY-MM-DD')
    )
      setValue('date_preset', 'next_month')
    else setValue('date_preset', '')
  }, [formData?.end_date, formData?.start_date, setValue])

  const recommendedBudget = watch(`use_recommended_budget`)

  useEffect(() => {
    if (recommendedBudget) setValue(`budget`, 5000)
  }, [recommendedBudget, setValue])

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'checkbox',
        header: () => 'Select',
        cell: (info) => (
          <input
            type="checkbox"
            checked={selectedAudiences.some(
              (audience) => audience.name === info.row.original.audience_name,
            )}
            onChange={() =>
              toggleAudienceSelection({
                audience_name: info.row.original.audience_name,
                size: info.row.original.audience_size,
              })
            }
          />
        ),
      }),
      columnHelper.accessor('audience_name', {
        header: 'Name',
        cell: (info) => (
          <Link
            className="font-bold text-primary-persian-blue"
            to={`../../../../customers/audiences/${info.row.original._id}/details`}
            target="_blank">
            {info.row.original.audience_name}
          </Link>
        ),
      }),
      columnHelper.accessor('audience_size', {
        header: 'Audience size',
        cell: (info) => {
          return formatNumber(info.getValue() || 0)
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created date',
        cell: (info) => {
          return dayjs(info.getValue()).format('MM/DD/YYYY')
        },
      }),
      columnHelper.display({
        id: 'expander',
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.getIsExpanded() ? 'Hide details' : 'Show details'}
            </button>
          ) : null
        },
      }),
    ],
    [selectedAudiences, toggleAudienceSelection],
  )

  const findDMA = async (zip: string): Promise<void> => {
    const { default: dmaZipCodesResponse } = await dmaZipCodes()
    const dma = get(
      dmaZipCodesResponse.filter((dma: { zip_code: string }) => dma.zip_code === zip),
      '[0]',
      {
        dma_description: 'DMA not found',
      },
    )
    setFoundDMA(dma.dma_description)
  }

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className={'flex flex-1 flex-col overflow-auto pb-20'}>
      <motion.div
        ref={(_ref) => {
          if (containerHeight === 0 && _ref?.clientHeight)
            setContainerHeight(_ref?.clientHeight ?? 0)
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={'flex flex-1 flex-col pt-8 pb-8'}>
        <div className={'flex flex-col px-12'}>
          <div className={'flex flex-col gap-5'}>
            <div className={'flex flex-col gap-5 border-2 rounded-xl p-5'}>
              <div className={'flex flex-row gap-3 items-center justify-between'}>
                <div className={'flex flex-row items-center gap-3'}>
                  <PinIcon />
                  <span className={'font-bold'}>Give your campaign a name</span>
                </div>
              </div>

              <div className={'flex flex-row gap-5 w-full'}>
                <TextInput
                  inverted={true}
                  label={''}
                  name={`campaign_name`}
                  rules={{ required: 'This field is required' }}
                  placeholder={'My awesome campaign'}
                />
              </div>
            </div>
            <div className={'flex flex-col gap-5 border-2 rounded-xl p-5'}>
              <div className={'flex flex-row items-center gap-5 w-full justify-between'}>
                <div className={'flex flex-col gap-4'}>
                  <div className={'flex flex-row items-center gap-5'}>
                    {<CalendarIcon />}
                    <span className={'font-bold'}>Campaign dates</span>
                  </div>
                  <span className={'text-xs text-primary-persian-blue'}>
                    Some suggested dates
                  </span>
                  <div className={'flex flex-row gap-5'}>
                    <Controller
                      name={'date_preset'}
                      render={({ field }) => (
                        <div className={'flex'}>
                          <input
                            {...field}
                            type="radio"
                            id={`date_preset-eom`}
                            name={'date_preset'}
                            value={'eom'}
                            className="hidden peer"
                            checked={formData.date_preset === 'eom'}
                          />
                          <label
                            htmlFor={`date_preset-eom`}
                            className={
                              'flex cursor-pointer items-center bg-primary-persian-blue/10 p-3 rounded-xl text-xs peer-checked:bg-white peer-checked:border-2 peer-checked:border-primary-persian-blue peer-checked:font-bold'
                            }>
                            Today to end of this month
                          </label>
                        </div>
                      )}
                    />
                    <Controller
                      name={'date_preset'}
                      render={({ field }) => (
                        <div className={'flex'}>
                          <input
                            {...field}
                            type="radio"
                            id={`date_preset-next_month`}
                            name={'date_preset'}
                            value={'next_month'}
                            className="hidden peer"
                            checked={formData.date_preset === 'next_month'}
                          />
                          <label
                            htmlFor={`date_preset-next_month`}
                            className={
                              'flex cursor-pointer items-center bg-primary-persian-blue/10 p-3 rounded-xl text-xs peer-checked:bg-white peer-checked:border-2 peer-checked:border-primary-persian-blue peer-checked:font-bold'
                            }>
                            Next month
                          </label>
                        </div>
                      )}
                    />
                  </div>
                  <span className={'font-bold'}>Or pick custom dates</span>
                </div>
              </div>
              <div className={'flex flex-row gap-5'}>
                <div className="w-1/2">
                  <TextInput
                    label={'Start date'}
                    name={'start_date'}
                    type={'date'}
                    rules={{ required: 'This field is required' }}
                  />
                </div>
                <div className="w-1/2">
                  <TextInput
                    label={'End date'}
                    name={'end_date'}
                    type={'date'}
                    rules={{ required: 'This field is required' }}
                  />
                </div>
              </div>
            </div>
            <div className={'flex flex-col gap-5 border-2 rounded-xl p-5'}>
              <div className={'flex flex-row items-center gap-5 w-full justify-between'}>
                <div className={'font-bold flex flex-row items-center gap-5'}>
                  {<DollarSign />}
                  {'Budget'}
                </div>
                <div className={'flex flex-row text-sm font-bold items-center gap-5'}>
                  <label>Use recommended budget?</label>
                  <ToggleInput name={`use_recommended_budget`} label={''} />
                </div>
              </div>
              <div className={'flex flex-col gap-5 w-full'}>
                <TextInput
                  inverted={true}
                  name={`budget`}
                  label={'Budget'}
                  placeholder={'5000'}
                  rules={{
                    required: formData.use_recommended_budget
                      ? false
                      : 'This field is required',
                  }}
                />
              </div>
            </div>
            <div className={'flex flex-col gap-5 border-2 rounded-xl p-5'}>
              <div className={'flex flex-col gap-2'}>
                <div
                  className={'flex flex-row items-center gap-5 w-full justify-between'}>
                  <div className={'font-bold flex flex-row items-center gap-5'}>
                    {<PinIcon />}
                    {'Location'}
                  </div>
                </div>
                <span className={'text-xs'}>
                  Choose your media market or enter any valid US zip code
                </span>
              </div>
              <div className={'flex flex-col gap-5 w-full'}>
                <CreatableSelectInput
                  name={'zip'}
                  options={dmaOptions}
                  isMulti={true}
                  rules={{ required: 'This field is required' }}
                />
                <AnimatePresence>
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      setShowDMATool((prevSetting) => !prevSetting)
                    }}
                    className={'text-xs self-start text-primary-persian-blue font-bold'}>
                    {showDMATool ? (
                      <p>
                        Click <u>here</u> to hide
                      </p>
                    ) : (
                      <p>
                        Not sure which DMA your zip code is? Click <u>here</u> to find
                        out!
                      </p>
                    )}
                  </button>

                  {showDMATool && (
                    <motion.div
                      className={`flex flex-row gap-3 items-center`}
                      initial={{ opacity: 0, y: -50 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { type: 'spring', damping: 20, stiffness: 150 },
                      }}
                      exit={{
                        opacity: 0,
                        y: 0,
                        transition: { duration: 2 },
                      }}>
                      <div className={'w-1/3'}>
                        <TextInput
                          name={'dma_region'}
                          inverted={true}
                          placeholder={'94227'}
                        />
                      </div>
                      <Button
                        onClick={(e) => {
                          e.preventDefault()
                          findDMA(formData.dma_region)
                        }}>
                        Find region
                      </Button>
                      {foundDMA}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <div className={'flex flex-col gap-5 border-2 rounded-xl p-5'}>
              <div className={'flex flex-row items-center gap-5 w-full justify-between'}>
                <div className={'flex flex-col items-center gap-3'}>
                  <div className={'flex flex-row items-center gap-5'}>
                    {<AudienceIcon />}
                    <div className={'flex flex-col gap-2'}>
                      <span className={'font-bold'}>Marketing audiences</span>
                      <span className={'text-xs'}>
                        Choose from your LinkOne custom audiences
                      </span>
                    </div>
                  </div>
                </div>
                <div className={'flex flex-row text-sm font-bold items-center gap-5'}>
                  <ToggleInput name={`use_custom_audience`} label={''} />
                </div>
              </div>
              {formData?.use_custom_audience && (
                <AnimatePresence>
                  <motion.div
                    className={'flex flex-col gap-3'}
                    initial={{ opacity: 0, y: -50 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { type: 'spring', damping: 20, stiffness: 150 },
                    }}
                    exit={{
                      opacity: 0,
                      y: -20,
                      transition: { duration: 2 },
                    }}>
                    <div className={`w-full border-b-2`}>
                      <Table
                        columns={columns}
                        filters={[]}
                        fetchFn={useGetAudiences}
                        hidePagination={true}
                        emptyLabel={'Build an audience to choose this option'}
                        getRowCanExpand={() => true}
                        renderSubComponent={AudienceSubComponentDetails}
                      />
                    </div>
                    <Button
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(
                          `/dashboard/agency/${dealership?.organization_id}/dealership/${dealership?.id}/customers/audiences/list/create/select-type`,
                        )
                      }}>
                      + Create a new audience
                    </Button>
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center bg-white flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            setStep(step - 1)
          }}>
          Back
        </Button>
        <CampaignProgressBar step={step} />
        <Button variant={'primary'} disabled={isLoading}>
          Continue
        </Button>
      </motion.div>
    </motion.form>
  )
}

export default CreateNewCampaignDetails
